/**
 * FANPLAZA — Scroll Reveal Web Component
 *
 * Usage in Liquid:
 *   <scroll-reveal>Content fades up on scroll</scroll-reveal>
 *   <scroll-reveal data-effect="fade">Fade only, no movement</scroll-reveal>
 *   <scroll-reveal data-effect="slide-left">Slide from left</scroll-reveal>
 *   <scroll-reveal data-stagger>Children animate one by one</scroll-reveal>
 *   <scroll-reveal data-delay="200">Delayed reveal</scroll-reveal>
 */
class ScrollReveal extends HTMLElement {
  connectedCallback() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      this.classList.add('is-visible');
      return;
    }

    const delay = parseInt(this.dataset.delay) || 0;
    const threshold = parseFloat(this.dataset.threshold) || 0.1;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              this.classList.add('is-visible');
            }, delay);
            this.observer.unobserve(this);
          }
        });
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

if (!customElements.get('scroll-reveal')) {
  customElements.define('scroll-reveal', ScrollReveal);
}

/**
 * FANPLAZA — Subtle Parallax
 *
 * Usage: <div class="fp-parallax" data-speed="0.15">
 * Speed: 0 = no movement, 0.3 = strong parallax
 */
(function() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const els = [];
  let ticking = false;

  function init() {
    document.querySelectorAll('.fp-parallax').forEach(el => {
      els.push({ el, speed: parseFloat(el.dataset.speed) || 0.1 });
    });
    if (els.length) window.addEventListener('scroll', onScroll, { passive: true });
  }

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  }

  function update() {
    const scrollY = window.scrollY;
    const vh = window.innerHeight;
    els.forEach(({ el, speed }) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < vh) {
        const center = rect.top + rect.height / 2 - vh / 2;
        el.style.transform = 'translateY(' + (center * speed * -1).toFixed(1) + 'px)';
      }
    });
    ticking = false;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * FANPLAZA — Smooth cursor-follow for product images
 *
 * Usage: <div class="fp-tilt" data-intensity="8">
 * Creates a subtle 3D tilt on mouse move
 */
(function() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if ('ontouchstart' in window) return;

  /* Cache tilt elements once — don't query on every mousemove */
  var tiltEls = [];
  function cacheTiltEls() {
    tiltEls = Array.from(document.querySelectorAll('.fp-tilt'));
  }

  function initTilt() {
    cacheTiltEls();
    if (!tiltEls.length) return;

    var ticking = false;
    var lastE = null;

    document.addEventListener('mousemove', function(e) {
      lastE = e;
      if (!ticking) {
        requestAnimationFrame(function() {
          tiltEls.forEach(function(el) {
            var rect = el.getBoundingClientRect();
            if (lastE.clientY < rect.top || lastE.clientY > rect.bottom ||
                lastE.clientX < rect.left || lastE.clientX > rect.right) {
              el.style.transform = '';
              return;
            }
            var intensity = parseFloat(el.dataset.intensity) || 6;
            var x = (lastE.clientX - rect.left) / rect.width - 0.5;
            var y = (lastE.clientY - rect.top) / rect.height - 0.5;
            el.style.transform = 'perspective(600px) rotateY(' + (x * intensity).toFixed(2) + 'deg) rotateX(' + (y * -intensity).toFixed(2) + 'deg)';
          });
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTilt);
  } else {
    initTilt();
  }
})();
